import { AuthUserPermission } from '@cibo/core'
import { PaginatedFieldsContext } from '@cibo/landmanager'
import { Can } from '@cibo/profile'
import {
  SelectedFieldsProgramLimitProvider,
  useCreateProgramEngagement,
  useCreateWorkflow,
  useProgramEngagements,
  usePrograms,
  useWorkflows,
} from '@cibo/programs'
import { Markdown, ResponsiveDialog } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  ButtonProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  List,
  Stack,
  Typography,
} from '@mui/material'
import { ENROLLMENT_ROUTES, PROGRAM_ROUTES } from 'navigation/types/routes'
import { propEq } from 'ramda'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import { ENGAGEMENT_ROUTES } from '../../navigation/types/engagementRoutes'
import { ProgramOptionItem } from './ProgramOptionItem'

type EnrollSelectedFieldsProps = { programId?: string; buttonProps: ButtonProps }

export const EnrollSelectedFields = ({
  programId: passedProgramId,
  buttonProps,
}: EnrollSelectedFieldsProps) => {
  const { t } = useTranslation('impact/EnrollSelectedFields')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [selectedProgramId, setSelectedProgramId] = useState<string | undefined>(passedProgramId)
  const createEngagement = useCreateProgramEngagement()
  const createWorkflow = useCreateWorkflow()
  const { selectedFields, selectedRowIds, ownedByUsers } = useContext(PaginatedFieldsContext)
  const programs = usePrograms()
  const navigate = useNavigate()

  const relevantWorkflows = useWorkflows(0, 1000, {
    programId: selectedProgramId,
    benchmark: ['enrollment'],
    filters: { userId: ownedByUsers[0] },
  })
  const existingWorkflow = relevantWorkflows.data?.items[0]

  const relevantEngagements = useProgramEngagements({
    filters: [
      {
        programId: selectedProgramId ? [selectedProgramId] : undefined,
        userId: ownedByUsers[0],
      },
    ],
  })
  const existingEngagement = relevantEngagements.data?.items[0]

  const availablePrograms = programs.data
    ?.filter(p => !passedProgramId || p.programId === selectedProgramId)
    .filter(p => selectedFields.filter(Boolean).every(f => f.availableForEnrollment(p.programId)))

  useEffect(() => {
    if (availablePrograms?.length === 1) {
      setSelectedProgramId(availablePrograms[0].programId)
    }
  }, [availablePrograms])

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (availablePrograms) {
      if (availablePrograms?.length === 1) {
        startProgram()
      } else {
        setPickerOpen(true)
      }
    }
  }

  const startProgram = useCallback(() => {
    if (!selectedProgramId || !programs.isFetched) return

    const selectedProgram = programs.data?.find(propEq('programId', selectedProgramId))

    switch (selectedProgram?.program.rulesVersion) {
      case 'v1':
        return createWorkflow
          .mutateAsync({
            benchmark: 'enrollment',
            doesUpsert: true,
            fields: selectedRowIds as string[],
            name: '',
            programId: selectedProgramId,
            // @todo: Type userId correctly
            userId: ownedByUsers[0],
          })
          .then(response => {
            navigate(generatePath(ENROLLMENT_ROUTES.DETAIL, { workflowId: response.data.id }), {
              state: { isCreating: true },
            })
          })

      case 'v2':
        return createEngagement
          .mutateAsync({
            userId: ownedByUsers[0],
            programId: selectedProgramId,
            fields: selectedRowIds as string[],
            doesUpsert: true,
          })
          .then(response => {
            const { id: engagementId } = response.data
            // @TODO simplify to the direct /engagements/id route when ready
            navigate(
              generatePath(`${PROGRAM_ROUTES.ENGAGEMENTS}/${ENGAGEMENT_ROUTES.ENGAGEMENT}`, {
                engagementId,
                programId: selectedProgramId,
              })
            )
          })
    }
  }, [selectedProgramId, ownedByUsers, selectedRowIds])

  const reset = () => {
    createEngagement.reset()
    createWorkflow.reset()
  }
  const loading = createEngagement.isLoading || createWorkflow.isLoading
  const error = createEngagement.error || createWorkflow.error

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <LoadingButton
          color="secondary"
          {...buttonProps}
          onClick={handleClick}
          disabled={
            !availablePrograms?.length ||
            ownedByUsers.length !== 1 ||
            selectedFields.some(f => !f.can(['field_enroll']))
          }
          data-testid="enroll-selected-fields"
        >
          {t('enrollNow', {
            count: availablePrograms?.length ?? 0,
            title: availablePrograms?.[0]?.program.content.fields.title,
            context: !!existingWorkflow || !!existingEngagement ? 'existing' : 'new',
          })}
        </LoadingButton>

        <Fade in={!availablePrograms?.length || ownedByUsers.length !== 1}>
          <Typography color="error" variant="caption">
            {t('enrollNowError', {
              context:
                ownedByUsers.length > 1
                  ? 'manyUsers'
                  : !availablePrograms?.length
                  ? 'noPrograms'
                  : undefined,
            })}
          </Typography>
        </Fade>
      </Stack>

      <ResponsiveDialog
        fullWidth
        maxWidth="xs"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
      >
        <DialogTitle>{t('enrollDialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Markdown>
              {t('whichProgram', {
                count: selectedFields.length,
                name: selectedFields?.[0]?.name,
              })}
            </Markdown>
            <List>
              {availablePrograms?.map(p => (
                <SelectedFieldsProgramLimitProvider key={p.programId} programId={p.programId}>
                  <ProgramOptionItem
                    program={p}
                    setProgramId={setSelectedProgramId}
                    selectedProgramId={selectedProgramId}
                    fieldCount={selectedFields.length}
                  />
                </SelectedFieldsProgramLimitProvider>
              ))}
            </List>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button color="secondary" onClick={() => setPickerOpen(false)}>
            {t('cancel')}
          </Button>
          <LoadingButton
            onClick={startProgram}
            loading={loading}
            variant="contained"
            color="primary"
            disabled={!selectedProgramId || relevantWorkflows.isFetching}
            data-testid="enroll-button"
          >
            {!!existingWorkflow || !!existingEngagement
              ? t('add', { count: selectedRowIds.length })
              : t('begin')}
          </LoadingButton>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={!!error} onClose={() => reset()}>
        <DialogTitle>{t('createWorkflowErrorTitle', { count: selectedFields.length })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Markdown>{t('createWorkflowErrorBody', { count: selectedFields.length })}</Markdown>
            <Can useAny={AuthUserPermission.DEBUG_USER}>
              <pre>{JSON.stringify(error, null, 3)}</pre>
            </Can>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => reset()}>
            {t('close')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  )
}
